import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Plus, FileDown, Trash2, Pencil, Briefcase, Banknote, TrendingUp, TrendingDown,
  Download, Search, Camera, ImageOff, QrCode, ClipboardCheck, CheckCircle2, AlertTriangle, History, Flag,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/Common";
import { StockTakeHistoryDialog } from "@/components/StockTakeHistory";
import { fmtMoney, fmtDate } from "@/lib/format";
import { DatePicker } from "@/components/DatePicker";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export const CATEGORIES = [
  { id: "office_furniture", label: "Office Furniture" },
  { id: "laptop", label: "Laptop" },
  { id: "desktop", label: "Desktop" },
  { id: "car_vehicle", label: "Car / Vehicle" },
  { id: "land_building", label: "Land / Building" },
  { id: "equipment", label: "Equipment" },
  { id: "other", label: "Other" },
];
const STATUSES = [
  { id: "in_use", label: "In use" },
  { id: "in_storage", label: "In storage" },
  { id: "under_repair", label: "Under repair" },
  { id: "disposed", label: "Disposed" },
  { id: "sold", label: "Sold" },
];
const CONDITIONS = ["new", "good", "fair", "poor"];
const CURRENCIES = ["USD", "IDR", "AUD", "EUR", "SGD"];

const catLabel = (id) => CATEGORIES.find((c) => c.id === id)?.label || id;

function StatusPill({ value }) {
  const map = {
    in_use: "bg-emerald-50 text-emerald-700 ring-emerald-200",
    in_storage: "bg-slate-100 text-slate-700 ring-slate-200",
    under_repair: "bg-amber-50 text-amber-800 ring-amber-200",
    disposed: "bg-red-50 text-red-700 ring-red-200",
    sold: "bg-sky-50 text-sky-700 ring-sky-200",
  };
  return (
    <span className={`inline-block px-2 py-0.5 rounded-md text-[11px] font-medium ring-1 ring-inset ${map[value] || map.in_storage}`}>
      {(value || "").replace("_", " ")}
    </span>
  );
}

function AssetThumb({ asset }) {
  const { data: url } = useQuery({
    queryKey: ["asset-photo", asset.id, asset.photo?.uploaded_at],
    queryFn: () => api.getAssetPhotoBlobUrl(asset.id),
    enabled: !!asset.photo?.storage_path,
    staleTime: Infinity,
  });
  if (!asset.photo?.storage_path) {
    return (
      <span className="size-9 shrink-0 grid place-items-center rounded-md bg-slate-100 text-slate-400">
        <ImageOff className="size-4" />
      </span>
    );
  }
  return url ? (
    <img src={url} alt={asset.name} data-testid={`asset-thumb-${asset.id}`}
         className="size-9 shrink-0 rounded-md object-cover border border-border" />
  ) : (
    <span className="size-9 shrink-0 rounded-md bg-slate-100 animate-pulse" />
  );
}

function currencyTotals(assets, field) {
  const sums = {};
  for (const a of assets) {
    const cur = a.currency || "USD";
    sums[cur] = (sums[cur] || 0) + (Number(a[field]) || 0);
  }
  const parts = Object.entries(sums).map(([c, v]) => `${fmtMoney(v)} ${c}`);
  return parts.length ? parts.join(" · ") : "0.00";
}

function downloadCSV(filename, assets) {
  if (!assets.length) return;
  const headers = ["Name", "Category", "Brand", "Model", "Serial / Plate / Title",
    "Year of manufacture", "Purchase date", "Purchase cost", "Book value",
    "Useful life (years)", "Salvage value", "Market value",
    "Currency", "Condition", "Location", "Assigned to", "Status", "Notes"];
  const esc = (v) => {
    const s = String(v ?? "").replace(/"/g, '""');
    return /[",\n]/.test(s) ? `"${s}"` : s;
  };
  const lines = [headers.join(","), ...assets.map((a) => [
    a.name, catLabel(a.category), a.brand, a.model, a.serial_no,
    a.year_of_manufacture ?? "", a.purchase_date ?? "", a.purchase_cost,
    a.book_value ?? a.purchase_cost, a.useful_life_effective ?? "",
    a.salvage_value ?? 0, a.market_value, a.currency, a.condition,
    a.location, a.assigned_to,
    (a.status || "").replace("_", " "), a.notes,
  ].map(esc).join(","))];
  const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const el = document.createElement("a");
  el.href = url; el.download = filename;
  document.body.appendChild(el); el.click(); el.remove();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

export default function CompanyAssets() {
  const qc = useQueryClient();
  const [catFilter, setCatFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [editAsset, setEditAsset] = useState(null); // null | {} (new) | asset
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [stockTake, setStockTake] = useState(null); // null | { scanned: Set, missingOnly: bool, startedAt }
  const [historyOpen, setHistoryOpen] = useState(false);
  const [finishOpen, setFinishOpen] = useState(false);
  const [finishNotes, setFinishNotes] = useState("");

  const finishMut = useMutation({
    mutationFn: () => api.finishStockTake({
      asset_ids: filtered.map((a) => a.id),
      scanned_ids: [...stockTake.scanned],
      scope_label: [
        catFilter === "all" ? "All assets" : catLabel(catFilter),
        search.trim() ? `search “${search.trim()}”` : "",
      ].filter(Boolean).join(" · "),
      started_at: stockTake.startedAt,
      notes: finishNotes,
    }),
    onSuccess: (st) => {
      toast.success(st.missing_count
        ? `Stock-take saved — ${st.missing_count} missing flagged`
        : "Stock-take saved — all assets accounted for");
      setFinishOpen(false); setFinishNotes(""); setStockTake(null);
      qc.invalidateQueries({ queryKey: ["stock-takes"] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not save stock-take"),
  });

  const { data: assets = [], isLoading } = useQuery({
    queryKey: ["company-assets"],
    queryFn: () => api.listAssets(),
  });

  // QR deep link: /assets?asset=<id> opens that asset's record after a scan.
  const [searchParams, setSearchParams] = useSearchParams();
  const linkedId = searchParams.get("asset");
  useEffect(() => {
    if (!linkedId || !assets.length) return;
    const found = assets.find((a) => a.id === linkedId);
    if (found) {
      setEditAsset(found);
    } else {
      toast.error("Scanned asset not found — it may have been deleted");
    }
    setSearchParams({}, { replace: true });
  }, [linkedId, assets]); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return assets.filter((a) => {
      if (catFilter !== "all" && a.category !== catFilter) return false;
      if (!q) return true;
      return [a.name, a.brand, a.model, a.serial_no, a.assigned_to, a.location]
        .some((v) => (v || "").toLowerCase().includes(q));
    });
  }, [assets, catFilter, search]);

  const deleteMut = useMutation({
    mutationFn: (id) => api.deleteCompanyAsset(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["company-assets"] });
      setConfirmDeleteId(null);
      toast.success("Asset deleted");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Delete failed"),
  });

  const scanned = stockTake?.scanned || new Set();
  const missing = stockTake ? filtered.filter((a) => !scanned.has(a.id)) : [];
  const displayRows = stockTake?.missingOnly ? missing : filtered;

  function toggleScanned(id) {
    setStockTake((s) => {
      const next = new Set(s.scanned);
      if (next.has(id)) next.delete(id); else next.add(id);
      return { ...s, scanned: next };
    });
  }

  function handleScanKey(e) {
    if (e.key !== "Enter") return;
    const v = e.target.value.trim();
    e.target.value = "";
    if (!v) return;
    const m = v.match(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i);
    const low = v.toLowerCase();
    const found =
      (m && assets.find((a) => a.id === m[0])) ||
      assets.find((a) => a.serial_no && a.serial_no.toLowerCase() === low) ||
      assets.find((a) => a.name.toLowerCase() === low);
    if (!found) {
      toast.error(`No asset matches "${v.slice(0, 40)}"`);
      return;
    }
    if (scanned.has(found.id)) {
      toast.info(`${found.name} — already ticked`);
      return;
    }
    setStockTake((s) => ({ ...s, scanned: new Set(s.scanned).add(found.id) }));
    toast.success(`✓ ${found.name}`);
  }

  return (
    <div data-testid="company-assets-page">
      <PageHeader
        overline="company property"
        title="Company Assets"
        actions={
          <div className="flex flex-wrap gap-2">
            <Button
              data-testid="assets-stocktake-btn"
              variant="outline"
              className={stockTake ? "border-indigo-400 bg-indigo-50 text-indigo-800" : ""}
              onClick={() => setStockTake(stockTake ? null : { scanned: new Set(), missingOnly: false, startedAt: new Date().toISOString() })}
            >
              <ClipboardCheck className="size-4 mr-1.5" />
              {stockTake ? "Exit stock-take" : "Stock-take"}
            </Button>
            <Button
              data-testid="assets-stocktake-history-btn"
              variant="outline"
              onClick={() => setHistoryOpen(true)}
            >
              <History className="size-4 mr-1.5" /> History
            </Button>
            <Button
              data-testid="assets-pdf-btn"
              variant="outline"
              onClick={() => api.openAssetRegisterPdf(
                catFilter !== "all" ? { category: catFilter } : {},
              )}
            >
              <FileDown className="size-4 mr-1.5" /> Register PDF
            </Button>
            <Button
              data-testid="assets-qr-btn"
              variant="outline"
              onClick={() => api.openAssetQrLabelsPdf(
                catFilter !== "all" ? { category: catFilter } : {},
              )}
            >
              <QrCode className="size-4 mr-1.5" /> QR labels
            </Button>
            <Button
              data-testid="assets-csv-btn"
              variant="outline"
              onClick={() => downloadCSV("asset-register.csv", filtered)}
            >
              <Download className="size-4 mr-1.5" /> CSV
            </Button>
            <Button
              data-testid="asset-add-btn"
              onClick={() => setEditAsset({})}
              className="bg-primary hover:bg-primary/90"
            >
              <Plus className="size-4 mr-1.5" /> Add asset
            </Button>
          </div>
        }
      />

      <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 mb-5">
        <AssetKpi testid="assets-kpi-count" label="Assets" icon={Briefcase}
                  value={filtered.length} tone="slate" />
        <AssetKpi testid="assets-kpi-cost" label="Total purchase cost" icon={Banknote}
                  value={currencyTotals(filtered, "purchase_cost")} tone="amber" small />
        <AssetKpi testid="assets-kpi-book" label="Total book value" icon={TrendingDown}
                  value={currencyTotals(filtered, "book_value")} tone="slate" small />
        <AssetKpi testid="assets-kpi-value" label="Total market value" icon={TrendingUp}
                  value={currencyTotals(filtered, "market_value")} tone="emerald" small />
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="assets-filters">
        {[{ id: "all", label: "All" }, ...CATEGORIES].map((c) => {
          const active = catFilter === c.id;
          const count = c.id === "all"
            ? assets.length
            : assets.filter((a) => a.category === c.id).length;
          return (
            <button
              key={c.id}
              data-testid={`assets-cat-${c.id}`}
              onClick={() => setCatFilter(c.id)}
              className={`inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-medium ring-1 ring-inset transition ${
                active
                  ? "bg-slate-800 text-white ring-slate-800"
                  : "bg-white text-slate-700 ring-slate-200 hover:bg-slate-50"
              }`}
            >
              {c.label}
              <span className={`text-[10px] mono px-1.5 py-0.5 rounded-full ${
                active ? "bg-white/25" : "bg-slate-100 text-slate-600"
              }`}>{count}</span>
            </button>
          );
        })}
        <div className="relative ml-auto">
          <Search className="size-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            data-testid="assets-search"
            className="pl-8 h-8 w-56 text-sm"
            placeholder="Search name, serial, holder…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {stockTake && (
        <div data-testid="stocktake-banner"
             className="mb-4 rounded-md border border-indigo-300 bg-indigo-50 p-4">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
            <div className="flex items-center gap-2 font-semibold text-indigo-900">
              <ClipboardCheck className="size-5" /> Stock-take in progress
            </div>
            <div className="flex items-center gap-3 text-sm">
              <span data-testid="stocktake-progress" className="mono text-indigo-900">
                <b>{scanned.size}</b> / {filtered.length} scanned
              </span>
              <span
                data-testid="stocktake-missing-count"
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-semibold ring-1 ring-inset ${
                  missing.length
                    ? "bg-red-50 text-red-700 ring-red-200"
                    : "bg-emerald-50 text-emerald-700 ring-emerald-200"
                }`}
              >
                {missing.length ? (
                  <><AlertTriangle className="size-3" /> {missing.length} missing</>
                ) : (
                  <><CheckCircle2 className="size-3" /> All accounted for</>
                )}
              </span>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Input
              data-testid="stocktake-scan-input"
              autoFocus
              className="max-w-md bg-white"
              placeholder="Scan a QR label (or type serial / exact name) and press Enter…"
              onKeyDown={handleScanKey}
            />
            <label className="flex items-center gap-2 text-xs text-indigo-900 cursor-pointer">
              <input
                type="checkbox"
                data-testid="stocktake-missing-toggle"
                className="size-4 accent-indigo-600"
                checked={!!stockTake.missingOnly}
                onChange={(e) => setStockTake({ ...stockTake, missingOnly: e.target.checked })}
              />
              Show missing only
            </label>
            <Button
              data-testid="stocktake-finish-btn"
              size="sm"
              className="ml-auto bg-indigo-600 hover:bg-indigo-700"
              disabled={!filtered.length}
              onClick={() => setFinishOpen(true)}
            >
              <Flag className="size-3.5 mr-1.5" /> Finish stock-take
            </Button>
          </div>
          <p className="text-[11px] text-indigo-800/70 mt-2">
            Tip: click any row to tick it off manually. Missing assets stay flagged in red.
          </p>
        </div>
      )}

      {isLoading ? (
        <p className="text-muted-foreground">Loading…</p>
      ) : filtered.length === 0 ? (
        <EmptyState
          title="No assets"
          hint="Register office furniture, laptops, vehicles, land and equipment"
          action={
            <Button data-testid="assets-empty-btn" onClick={() => setEditAsset({})}
                    className="bg-primary hover:bg-primary/90">
              <Plus className="size-4 mr-1.5" /> Add asset
            </Button>
          }
        />
      ) : (
        <div className="border border-border rounded-md bg-white overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent border-b border-border">
                <TableHead className="overline">Asset</TableHead>
                <TableHead className="overline">Category</TableHead>
                <TableHead className="overline text-right">Year</TableHead>
                <TableHead className="overline">Purchased</TableHead>
                <TableHead className="overline text-right">Cost</TableHead>
                <TableHead className="overline text-right">Book value</TableHead>
                <TableHead className="overline text-right">Market value</TableHead>
                <TableHead className="overline">Holder / Location</TableHead>
                <TableHead className="overline">Status</TableHead>
                <TableHead className="overline text-right w-24">&nbsp;</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {displayRows.map((a) => (
                <TableRow
                  key={a.id}
                  data-testid={`asset-row-${a.id}`}
                  className={`cursor-pointer ${
                    stockTake
                      ? scanned.has(a.id)
                        ? "bg-emerald-50/70 hover:bg-emerald-50"
                        : "bg-red-50/40 hover:bg-red-50/70"
                      : ""
                  }`}
                  onClick={() => (stockTake ? toggleScanned(a.id) : setEditAsset(a))}
                >
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-2.5">
                      <AssetThumb asset={a} />
                      <div className="min-w-0">
                        <span className="inline-flex items-center gap-1.5">
                          {stockTake && (
                            scanned.has(a.id) ? (
                              <CheckCircle2 data-testid={`stocktake-tick-${a.id}`} className="size-4 text-emerald-600 shrink-0" />
                            ) : (
                              <AlertTriangle data-testid={`stocktake-flag-${a.id}`} className="size-4 text-red-500 shrink-0" />
                            )
                          )}
                          {a.name}
                        </span>
                        {(a.brand || a.model || a.serial_no) && (
                          <div className="text-[10px] text-muted-foreground">
                            {[a.brand, a.model, a.serial_no].filter(Boolean).join(" · ")}
                          </div>
                        )}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="text-xs">{catLabel(a.category)}</TableCell>
                  <TableCell className="text-right mono text-xs">
                    {a.year_of_manufacture || "—"}
                  </TableCell>
                  <TableCell className="mono text-xs">
                    {a.purchase_date ? fmtDate(a.purchase_date) : "—"}
                  </TableCell>
                  <TableCell className="text-right mono text-xs">
                    {fmtMoney(a.purchase_cost)} {a.currency}
                  </TableCell>
                  <TableCell className="text-right mono text-xs" data-testid={`asset-book-${a.id}`}>
                    {fmtMoney(a.book_value ?? a.purchase_cost)} {a.currency}
                    {Number(a.depreciation_pct) > 0 && (
                      <div className="text-[10px] text-muted-foreground">
                        −{a.depreciation_pct}%
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="text-right mono font-semibold">
                    {fmtMoney(a.market_value)} {a.currency}
                  </TableCell>
                  <TableCell className="text-xs">
                    {[a.assigned_to, a.location].filter(Boolean).join(" — ") || "—"}
                  </TableCell>
                  <TableCell><StatusPill value={a.status} /></TableCell>
                  <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                    <div className="inline-flex gap-1">
                      <Button data-testid={`asset-qr-${a.id}`} variant="ghost" size="sm"
                              className="h-7 w-7 p-0" title="Print QR label"
                              onClick={() => api.openAssetQrLabelPdf(a.id)}>
                        <QrCode className="size-3.5" />
                      </Button>
                      <Button data-testid={`asset-edit-${a.id}`} variant="ghost" size="sm"
                              className="h-7 w-7 p-0" onClick={() => setEditAsset(a)}>
                        <Pencil className="size-3.5" />
                      </Button>
                      <Button data-testid={`asset-delete-${a.id}`} variant="ghost" size="sm"
                              className="text-red-600 hover:text-red-700 h-7 w-7 p-0"
                              onClick={() => setConfirmDeleteId(a.id)}>
                        <Trash2 className="size-3.5" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <AssetDialog
        key={editAsset?.id || (editAsset ? "asset-new" : "asset-closed")}
        asset={editAsset}
        onClose={() => setEditAsset(null)}
        onSaved={() => {
          qc.invalidateQueries({ queryKey: ["company-assets"] });
          setEditAsset(null);
        }}
      />

      <StockTakeHistoryDialog open={historyOpen} onClose={() => setHistoryOpen(false)} />

      <Dialog open={finishOpen} onOpenChange={(v) => !v && setFinishOpen(false)}>
        <DialogContent data-testid="stocktake-finish-dialog">
          <DialogHeader>
            <DialogTitle>Finish this stock-take?</DialogTitle>
            <DialogDescription className="text-xs">
              Saves a permanent record: <b>{scanned.size}</b> of <b>{filtered.length}</b> assets checked
              {missing.length ? <>, <b className="text-red-700">{missing.length} missing</b></> : ", none missing"}
              {catFilter !== "all" ? ` — scope: ${catLabel(catFilter)}` : ""}.
            </DialogDescription>
          </DialogHeader>
          <Textarea data-testid="stocktake-finish-notes" rows={2} value={finishNotes}
                    onChange={(e) => setFinishNotes(e.target.value)}
                    placeholder="Notes (optional) — e.g. Dili office, Q3 audit" />
          <DialogFooter>
            <Button variant="outline" data-testid="stocktake-finish-cancel" onClick={() => setFinishOpen(false)}>Cancel</Button>
            <Button data-testid="stocktake-finish-confirm" className="bg-indigo-600 hover:bg-indigo-700"
                    disabled={finishMut.isPending} onClick={() => finishMut.mutate()}>
              {finishMut.isPending ? "Saving…" : "Save stock-take"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDeleteId} onOpenChange={(v) => !v && setConfirmDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this asset?</AlertDialogTitle>
            <AlertDialogDescription>
              It will be removed from the asset register permanently.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="asset-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="asset-delete-confirm"
                               className="bg-red-600 hover:bg-red-700"
                               onClick={() => deleteMut.mutate(confirmDeleteId)}>
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function AssetKpi({ testid, label, icon: Icon, value, tone, small }) {
  const toneMap = {
    slate: "border-slate-200 bg-white",
    amber: "border-amber-200 bg-amber-50",
    emerald: "border-emerald-200 bg-emerald-50",
  };
  const iconMap = {
    slate: "text-slate-700 bg-slate-100",
    amber: "text-amber-800 bg-amber-100",
    emerald: "text-emerald-700 bg-emerald-100",
  };
  return (
    <div data-testid={testid}
         className={`rounded-md border p-4 flex items-center justify-between gap-3 ${toneMap[tone]}`}>
      <div className="min-w-0">
        <p className="overline">{label}</p>
        <p className={`font-bold text-foreground mt-1 font-mono truncate ${small ? "text-base" : "text-2xl"}`}>
          {value}
        </p>
      </div>
      <span className={`size-10 shrink-0 grid place-items-center rounded-full ${iconMap[tone]}`}>
        <Icon className="size-5" />
      </span>
    </div>
  );
}

function PhotoSection({ asset }) {
  const qc = useQueryClient();
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [photo, setPhoto] = useState(asset.photo || null);
  const { data: url } = useQuery({
    queryKey: ["asset-photo", asset.id, photo?.uploaded_at],
    queryFn: () => api.getAssetPhotoBlobUrl(asset.id),
    enabled: !!photo?.storage_path,
    staleTime: Infinity,
  });

  async function upload(file) {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Only image files are allowed");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Photo too large (max 5 MB)");
      return;
    }
    setBusy(true);
    try {
      const res = await api.uploadAssetPhoto(asset.id, file);
      setPhoto(res.photo);
      qc.invalidateQueries({ queryKey: ["company-assets"] });
      toast.success("Photo uploaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    setBusy(true);
    try {
      await api.deleteAssetPhoto(asset.id);
      setPhoto(null);
      qc.invalidateQueries({ queryKey: ["company-assets"] });
      toast.success("Photo removed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Remove failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex items-center gap-3">
      {photo?.storage_path && url ? (
        <img src={url} alt={asset.name} data-testid="asset-photo-preview"
             className="size-20 rounded-md object-cover border border-border" />
      ) : (
        <span className="size-20 grid place-items-center rounded-md bg-slate-100 text-slate-400 border border-dashed border-slate-300">
          <Camera className="size-6" />
        </span>
      )}
      <div className="space-y-1.5">
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          className="hidden"
          data-testid="asset-photo-input"
          onChange={(e) => {
            upload(e.target.files?.[0]);
            e.target.value = "";
          }}
        />
        <Button type="button" variant="outline" size="sm" disabled={busy}
                data-testid="asset-photo-upload"
                onClick={() => fileRef.current?.click()}>
          <Camera className="size-3.5 mr-1.5" />
          {busy ? "Working…" : photo ? "Replace photo" : "Add photo"}
        </Button>
        {photo && (
          <Button type="button" variant="ghost" size="sm" disabled={busy}
                  className="text-red-600 hover:text-red-700 block"
                  data-testid="asset-photo-remove" onClick={remove}>
            <Trash2 className="size-3.5 mr-1.5" /> Remove
          </Button>
        )}
      </div>
    </div>
  );
}

function AssetDialog({ asset, onClose, onSaved }) {
  const isNew = asset && !asset.id;
  const [form, setForm] = useState(() => asset ? {
    name: asset.name || "",
    category: asset.category || "",
    brand: asset.brand || "",
    model: asset.model || "",
    serial_no: asset.serial_no || "",
    year_of_manufacture: asset.year_of_manufacture ?? "",
    purchase_date: asset.purchase_date || "",
    purchase_cost: asset.purchase_cost ?? 0,
    market_value: asset.market_value ?? 0,
    salvage_value: asset.salvage_value ?? 0,
    useful_life_years: asset.useful_life_years ?? "",
    currency: asset.currency || "USD",
    condition: asset.condition || "good",
    location: asset.location || "",
    assigned_to: asset.assigned_to || "",
    status: asset.status || "in_use",
    notes: asset.notes || "",
  } : null);
  const [busy, setBusy] = useState(false);
  if (!asset || !form) return null;

  const set = (patch) => setForm({ ...form, ...patch });

  async function save() {
    if (!form.name.trim()) { toast.error("Asset name is required"); return; }
    if (!form.category) { toast.error("Pick a category"); return; }
    const body = {
      ...form,
      name: form.name.trim(),
      year_of_manufacture: form.year_of_manufacture === "" ? null : Number(form.year_of_manufacture),
      useful_life_years: form.useful_life_years === "" ? null : Number(form.useful_life_years),
      purchase_cost: Number(form.purchase_cost) || 0,
      market_value: Number(form.market_value) || 0,
      salvage_value: Number(form.salvage_value) || 0,
    };
    setBusy(true);
    try {
      if (isNew) {
        const created = await api.createAsset(body);
        toast.success(`${created.name} registered`);
      } else {
        const updated = await api.updateCompanyAsset(asset.id, body);
        toast.success(`${updated.name} updated`);
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={!!asset} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="asset-dialog" className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isNew ? "Register an asset" : `Edit — ${asset.name}`}</DialogTitle>
          <DialogDescription className="text-xs">
            Company property register — furniture, IT kit, vehicles, land and equipment.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="sm:col-span-2">
            <Label className="overline">Asset name *</Label>
            <Input data-testid="asset-name" value={form.name}
                   onChange={(e) => set({ name: e.target.value })}
                   placeholder="e.g. Toyota Hilux double cab" />
          </div>
          <div>
            <Label className="overline">Category *</Label>
            <Select value={form.category || undefined} onValueChange={(v) => set({ category: v })}>
              <SelectTrigger data-testid="asset-category">
                <SelectValue placeholder="Select…" />
              </SelectTrigger>
              <SelectContent>
                {CATEGORIES.map((c) => (
                  <SelectItem key={c.id} value={c.id} data-testid={`asset-cat-opt-${c.id}`}>
                    {c.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="overline">Status</Label>
            <Select value={form.status} onValueChange={(v) => set({ status: v })}>
              <SelectTrigger data-testid="asset-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                {STATUSES.map((s) => (
                  <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="overline">Brand</Label>
            <Input data-testid="asset-brand" value={form.brand}
                   onChange={(e) => set({ brand: e.target.value })} />
          </div>
          <div>
            <Label className="overline">Model</Label>
            <Input data-testid="asset-model" value={form.model}
                   onChange={(e) => set({ model: e.target.value })} />
          </div>
          <div>
            <Label className="overline">Serial / plate / title no.</Label>
            <Input data-testid="asset-serial" value={form.serial_no}
                   onChange={(e) => set({ serial_no: e.target.value })} />
          </div>
          <div>
            <Label className="overline">Year of manufacture</Label>
            <Input data-testid="asset-year" type="number" min="1900"
                   max={new Date().getFullYear() + 1}
                   value={form.year_of_manufacture}
                   onChange={(e) => set({ year_of_manufacture: e.target.value })}
                   placeholder="e.g. 2022" />
          </div>
          <div>
            <Label className="overline">Purchase date</Label>
            <DatePicker testId="asset-purchase-date" value={form.purchase_date || ""}
                        onChange={(v) => set({ purchase_date: v })} />
          </div>
          <div>
            <Label className="overline">Currency</Label>
            <Select value={form.currency} onValueChange={(v) => set({ currency: v })}>
              <SelectTrigger data-testid="asset-currency"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="overline">Purchase cost</Label>
            <Input data-testid="asset-cost" type="number" min="0" step="0.01"
                   value={form.purchase_cost}
                   onChange={(e) => set({ purchase_cost: e.target.value })} />
          </div>
          <div>
            <Label className="overline">Current market value</Label>
            <Input data-testid="asset-market-value" type="number" min="0" step="0.01"
                   value={form.market_value}
                   onChange={(e) => set({ market_value: e.target.value })} />
          </div>
          <div>
            <Label className="overline">Useful life (years)</Label>
            <Input data-testid="asset-useful-life" type="number" min="0" max="100"
                   value={form.useful_life_years}
                   onChange={(e) => set({ useful_life_years: e.target.value })}
                   placeholder="Default per category" />
            <p className="text-[10px] text-muted-foreground mt-1">
              Straight-line book value. Blank uses the category default
              (laptop 3y · desktop 4y · furniture 10y · vehicle 8y · equipment 5y · land none).
            </p>
          </div>
          <div>
            <Label className="overline">Salvage value</Label>
            <Input data-testid="asset-salvage" type="number" min="0" step="0.01"
                   value={form.salvage_value}
                   onChange={(e) => set({ salvage_value: e.target.value })} />
          </div>
          <div>
            <Label className="overline">Condition</Label>
            <Select value={form.condition} onValueChange={(v) => set({ condition: v })}>
              <SelectTrigger data-testid="asset-condition"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CONDITIONS.map((c) => (
                  <SelectItem key={c} value={c}>{c[0].toUpperCase() + c.slice(1)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="overline">Assigned to</Label>
            <Input data-testid="asset-assigned" value={form.assigned_to}
                   onChange={(e) => set({ assigned_to: e.target.value })}
                   placeholder="e.g. João Pereira" />
          </div>
          <div>
            <Label className="overline">Location</Label>
            <Input data-testid="asset-location" value={form.location}
                   onChange={(e) => set({ location: e.target.value })}
                   placeholder="e.g. Dili office, 2nd floor" />
          </div>
          <div className="sm:col-span-2">
            <Label className="overline">Notes</Label>
            <Textarea data-testid="asset-notes" rows={2} value={form.notes}
                      onChange={(e) => set({ notes: e.target.value })} />
          </div>
          {!isNew && (
            <div className="sm:col-span-2">
              <Label className="overline">Photo</Label>
              <PhotoSection asset={asset} />
            </div>
          )}
          {isNew && (
            <p className="sm:col-span-2 text-[10px] text-muted-foreground">
              Save the asset first, then reopen it to attach a photo.
            </p>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="asset-cancel">Cancel</Button>
          <Button data-testid="asset-save" className="bg-primary hover:bg-primary/90"
                  disabled={busy} onClick={save}>
            {busy ? "Saving…" : isNew ? "Register asset" : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
